import { Component, DestroyRef, inject, signal, WritableSignal, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { TranslocoDirective } from '@jsverse/transloco';
import { LayoutService } from '../../../../../../../../@vex/services/layout.service';
import {
	ReallocateTarget,
	TenantUnitReference,
	TenantUnitReferenceService,
	TenantUnitUserReference,
	TenantUnitUserReferenceService,
} from '@eadsysteme/billingclient-v1';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SessionService } from '../../../../../../../services/session.service';
import { SnackbarService } from '../../../../../../../services/snackbar.service';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatOption, MatSelect } from '@angular/material/select';
import { DateUtil } from '../../../../../../../util/DateUtil';
import { CdkDrag, CdkDragHandle } from '@angular/cdk/drag-drop';

@Component({
	selector: 'ara-reallocate-user-dialog',
	imports: [
		ReactiveFormsModule,
		MatFormField,
		MatLabel,
		MatDialogTitle,
		TranslocoDirective,
		MatButton,
		MatIcon,
		MatDialogContent,
		MatDialogActions,
		MatSelect,
		MatOption,
		FormsModule,
		CdkDrag,
		CdkDragHandle,
	],
	templateUrl: './reallocate-user-dialog.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrl: './reallocate-user-dialog.component.scss',
})
export class ReallocateUserDialogComponent {
	protected layoutService = inject(LayoutService);
	dialogRef = inject<MatDialogRef<ReallocateUserDialogComponent>>(MatDialogRef);
	private sessionService = inject(SessionService);
	private tenantUnitReferenceService = inject(TenantUnitReferenceService);
	private tenantUnitUserReferenceService = inject(TenantUnitUserReferenceService);
	private snackBar = inject(SnackbarService);
	protected dateUtil = inject(DateUtil);

	private destroyRef: DestroyRef = inject(DestroyRef);
	protected instanceToMigrate: WritableSignal<TenantUnitReference> = signal({ exportId: '' });
	protected usersOfTargetInstance: WritableSignal<TenantUnitUserReference[]> = signal([]);
	protected targetInstance: FormControl<TenantUnitReference> = new FormControl<TenantUnitReference>({ value: { exportId: '' }, disabled: false });
	protected targetUser: FormControl<TenantUnitUserReference> = new FormControl<TenantUnitUserReference>({ value: null, disabled: false });
	protected userToMigrate: FormControl<TenantUnitUserReference> = new FormControl<TenantUnitUserReference>({ value: null, disabled: false });
	protected instancesInSync: TenantUnitReference[] = [];
	private billingId: string;

	constructor() {
		const billing = inject(MAT_DIALOG_DATA);

		this.billingId = billing.billingId;
		this.instanceToMigrate.set(billing.instanceToMigrate);
		if (billing.instanceToMigrate.tenantUnitUserReferences?.length === 1) {
			this.userToMigrate.setValue(billing.instanceToMigrate.tenantUnitUserReferences[0]);
		}

		this.instancesInSync = [];
		this.tenantUnitReferenceService
			.readAllTenantUnitReference(this.sessionService.effectiveArea, this.billingId)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (value) => {
					this.instancesInSync = value.filter((currentInstance) => currentInstance.inSync);
				},
			});

		this.targetInstance.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
			if (!value) {
				return;
			}
			this.tenantUnitUserReferenceService
				.readAllTenantUnitUserReference(this.sessionService.effectiveArea, this.billingId, value.exportId)
				.pipe(takeUntilDestroyed(this.destroyRef))
				.subscribe({
					next: (users) => {
						users = users.filter((users) => users.inSync);
						if (users.length > 0) {
							this.usersOfTargetInstance.set(users);
							return;
						}
						this.snackBar.error('errors.loadingBillingUserRoles');
						this.targetInstance.setValue(null);
						this.targetUser.setValue(null);
						this.usersOfTargetInstance.set([]);
					},
					error: () => {
						this.snackBar.error('errors.loadingBillingUserRoles');
						this.targetUser.setValue(null);
						this.usersOfTargetInstance.set([]);
					},
				});
		});
	}

	close(toSave: boolean) {
		if (!toSave) {
			this.dialogRef.close();
			return;
		}
		const target: ReallocateTarget = { targetExportId: this.targetUser.value.exportId };
		this.dialogRef.close({ sourceExportId: this.userToMigrate.value.exportId, target });
	}
}
